import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaBan, FaUnlock, FaWallet, FaTrash, FaUndo, FaStore, FaTimes } from 'react-icons/fa';
import { useShopOwners } from '../hooks/useShopOwners';
import { formatDate, formatMoney } from '../components/shops/shopConstants';
import '../styles/shops.scss';

const STATUS_FILTERS = [
    { value: '', label: 'الكل' },
    { value: 'active', label: 'نشط' },
    { value: 'unverified', label: 'غير موثق' },
    { value: 'banned', label: 'محظور' },
    { value: 'deleted', label: 'محذوف' },
];

const BalanceModal = ({ owner, onSave, onClose }) => {
    const [direction, setDirection] = useState('credit');
    const [amount, setAmount] = useState('');
    const value = Number(amount);
    const valid = Number.isFinite(value) && value > 0 && (direction === 'credit' || value <= owner.balance);
    return (
        <div className="shops-modal-overlay" onClick={onClose}>
            <form className="shops-modal" onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); if (valid) onSave(direction === 'credit' ? value : -value); }}>
                <button type="button" className="modal-close" onClick={onClose} aria-label="إغلاق"><FaTimes /></button>
                <h2>تعديل رصيد {owner.ownerName}</h2>
                <div className="muted">الرصيد الحالي: <strong>{formatMoney(owner.balance)}</strong></div>
                <div className="segmented full">
                    <button type="button" className={direction === 'credit' ? 'active' : ''} onClick={() => setDirection('credit')}>إضافة</button>
                    <button type="button" className={direction === 'debit' ? 'active' : ''} onClick={() => setDirection('debit')}>خصم</button>
                </div>
                <label className="field-label">المبلغ (د.ل)
                    <input type="number" min="0" step="0.5" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus required />
                </label>
                {direction === 'debit' && value > owner.balance && <div className="reason-note">لا يمكن خصم أكثر من الرصيد الحالي</div>}
                <div className="modal-actions">
                    <button type="submit" className="btn primary" disabled={!valid}>حفظ</button>
                    <button type="button" className="btn ghost" onClick={onClose}>إلغاء</button>
                </div>
            </form>
        </div>
    );
};

export const ShopOwners = () => {
    const navigate = useNavigate();
    const { owners, total, page, pageSize, summary, isLoading, query, setQuery, toggleBan, adjustBalance, deleteOwner, reactivateOwner } = useShopOwners();
    const [search, setSearch] = useState(query.q);
    const [balanceOwner, setBalanceOwner] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => setQuery((prev) => (prev.q === search ? prev : { ...prev, q: search, page: 1 })), 350);
        return () => clearTimeout(t);
    }, [search, setQuery]);

    const pages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <section className="container shops shop-owners">
            <div className="shops-header">
                <div>
                    <h1>أصحاب الورش</h1>
                    <div className="header-stats">
                        <span><strong>{summary?.owners ?? '—'}</strong> حساب</span>
                        <span><strong>{summary ? formatMoney(summary.balance) : '—'}</strong> إجمالي الأرصدة</span>
                    </div>
                </div>
            </div>

            <div className="panel">
                <div className="panel-toolbar filters">
                    <div className="search-box">
                        <FaSearch />
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث بالاسم أو رقم الهاتف" />
                    </div>
                    <div className="segmented">
                        {STATUS_FILTERS.map((s) => (
                            <button type="button" key={s.value} className={query.status === s.value ? 'active' : ''} onClick={() => setQuery({ ...query, status: s.value, page: 1 })}>
                                {s.label}
                            </button>
                        ))}
                    </div>
                </div>

                {isLoading && owners.length === 0 ? (
                    <div className="loading">جاري التحميل...</div>
                ) : owners.length === 0 ? (
                    <div className="empty-state">لا يوجد أصحاب ورش مطابقون</div>
                ) : (
                    <div className="table-wrap">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>الاسم</th>
                                    <th>الهاتف</th>
                                    <th>الحالة</th>
                                    <th>الرصيد</th>
                                    <th>الورش</th>
                                    <th>إعلانات نشطة</th>
                                    <th>تاريخ الانضمام</th>
                                    <th>إجراءات</th>
                                </tr>
                            </thead>
                            <tbody>
                                {owners.map((o) => (
                                    <tr key={o._id} className={o.isDeleted ? 'row-muted' : ''}>
                                        <td><strong>{o.ownerName}</strong></td>
                                        <td><bdi>{o.phoneNumber}</bdi></td>
                                        <td>
                                            {o.isDeleted ? <span className="status-chip tone-muted">محذوف</span>
                                                : o.isBanned ? <span className="status-chip tone-danger">محظور</span>
                                                    : o.isVerified ? <span className="status-chip tone-success">موثق</span>
                                                        : <span className="status-chip tone-warning">غير موثق</span>}
                                        </td>
                                        <td className="num">{formatMoney(o.balance)}</td>
                                        <td>
                                            <span className="num">{o.shopsTotal}</span>
                                            {o.shops?.pending > 0 && <span className="status-chip tone-warning small-chip">{o.shops.pending} قيد المراجعة</span>}
                                        </td>
                                        <td className="num">{o.activeAds}</td>
                                        <td>{formatDate(o.createdAt)}</td>
                                        <td>
                                            {deletingId === o._id ? (
                                                <div className="row-actions">
                                                    <button type="button" className="btn danger small" onClick={async () => { if (await deleteOwner(o._id, 'حذف بواسطة الإدارة')) setDeletingId(null); }}>تأكيد الحذف</button>
                                                    <button type="button" className="btn ghost small" onClick={() => setDeletingId(null)}>إلغاء</button>
                                                </div>
                                            ) : (
                                                <div className="row-actions">
                                                    <button type="button" className="icon-btn" title="عرض الورش" onClick={() => navigate(`/shops?owner=${o._id}`)}><FaStore /></button>
                                                    <button type="button" className="icon-btn" title="تعديل الرصيد" onClick={() => setBalanceOwner(o)}><FaWallet /></button>
                                                    {!o.isDeleted && (
                                                        <button type="button" className={`icon-btn ${o.isBanned ? 'success' : 'warning'}`} title={o.isBanned ? 'رفع الحظر' : 'حظر'} onClick={() => toggleBan(o)}>
                                                            {o.isBanned ? <FaUnlock /> : <FaBan />}
                                                        </button>
                                                    )}
                                                    {o.isDeleted ? (
                                                        <button type="button" className="icon-btn success" title="إعادة تفعيل" onClick={() => reactivateOwner(o._id)}><FaUndo /></button>
                                                    ) : (
                                                        <button type="button" className="icon-btn danger" title="حذف" onClick={() => setDeletingId(o._id)}><FaTrash /></button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {pages > 1 && (
                    <div className="pagination">
                        <button type="button" disabled={page <= 1} onClick={() => setQuery({ ...query, page: page - 1 })}>السابق</button>
                        <span>{page} / {pages}</span>
                        <button type="button" disabled={page >= pages} onClick={() => setQuery({ ...query, page: page + 1 })}>التالي</button>
                    </div>
                )}
            </div>

            {balanceOwner && (
                <BalanceModal
                    owner={balanceOwner}
                    onClose={() => setBalanceOwner(null)}
                    onSave={async (amount) => { if (await adjustBalance(balanceOwner._id, amount)) setBalanceOwner(null); }}
                />
            )}
        </section>
    );
};
